import { Spin, notification } from 'antd';
import { PrivateRoutes } from 'components/PrivateRoutes';
import { onMessageListener } from 'firebase-config';
import { useMixpanel } from 'hooks/useMixpanel';
import { Groups } from 'pages/AccessManage/Groups';
import { Modules } from 'pages/AccessManage/Modules';
import { Permissions } from 'pages/AccessManage/Permissions';
import { Roles } from 'pages/AccessManage/Roles';
import { RolesFormPage } from 'pages/AccessManage/RolesFormPage';
import { Tribes } from 'pages/AccessManage/Tribes';
import { Users } from 'pages/AccessManage/Users';
import { Accruals } from 'pages/Accruals';
import { ActSverki } from 'pages/ActSverki';
import { BankStatements } from 'pages/BankStatements';
import { ClientsLogs } from 'pages/Clients/ClientsLogs';
import { Employees } from 'pages/Clients/Employees';
import { EmployeesRecords } from 'pages/Clients/EmployeesRecords';
import { Diagnostic } from 'pages/Diagnostic';
import { EdoAlertConfig } from 'pages/Edo';
import { BVU } from 'pages/Handbooks/BVU';
import { Balance } from 'pages/Handbooks/Balance';
import { BankAccounts } from 'pages/Handbooks/BankAccounts';
import { Contractors } from 'pages/Handbooks/Contractors';
import { GSVS } from 'pages/Handbooks/GSVS';
import { HandbookCalendar } from 'pages/Handbooks/HandbookCalendar';
import { KATO } from 'pages/Handbooks/KATO';
import { KBK } from 'pages/Handbooks/KBK';
import { KNP } from 'pages/Handbooks/KNP';
import { OKED } from 'pages/Handbooks/OKED';
import { PRP } from 'pages/Handbooks/PRP';
import { PartnerAccounts } from 'pages/Handbooks/PartnerAccounts';
import { Partners } from 'pages/Handbooks/Partners';
import { ProductServices } from 'pages/Handbooks/ProductServices';
import { RRP } from 'pages/Handbooks/RRP';
import { UGD } from 'pages/Handbooks/UGD';
import { HistorySessions } from 'pages/HistorySessions';
import { BCC } from 'pages/Integration/BCC';
import { Firebase } from 'pages/Integration/Firebase';
import { KGD } from 'pages/Integration/KGD';
import { SMS } from 'pages/Integration/SMS';
import { LoginPage } from 'pages/LoginPage';
import { AppVersion } from 'pages/Mobile/AppVersion';
import { Kassa } from 'pages/Mobile/Kassa';
import { MainEvents } from 'pages/Mobile/MainEvents';
import { Stories } from 'pages/Mobile/Stories';
import { StoryGroup } from 'pages/Mobile/StoryGroup';
import { NoAccessPage } from 'pages/NoAccessPage';
import { NotFoundPage } from 'pages/NotFoundPage';
import { PayedTaxes } from 'pages/PayedTaxes';
import { BccPayments } from 'pages/Payments/BccPayments';
import { BccPaymentsRefund } from 'pages/Payments/BccPaymentsRefund';
import { CardPayments } from 'pages/Payments/CardPayments';
import { CardReversalPayments } from 'pages/Payments/CardReversalPayments';
import { ClientPayments } from 'pages/Payments/ClientPayments';
import { PushNotification } from 'pages/PushManager/PushNotification';
import { Segments } from 'pages/PushManager/Segments';
import { RecoveryPage } from 'pages/RecoveryPage';
import { Discount } from 'pages/Services/Discount';
import { History } from 'pages/Services/History';
import { NewServices } from 'pages/Services/NewServices';
import { Options } from 'pages/Services/Options';
import { Package } from 'pages/Services/Package';
import { PackageCreate } from 'pages/Services/PackageCreate';
import { PackageEdit } from 'pages/Services/PackageEdit';
import { Promo } from 'pages/Services/Promo';
import { Promocodes } from 'pages/Services/Promocodes';
import { Sales } from 'pages/Statistics/Sales';
import { Subscribers } from 'pages/Subscribe';
import { Turns } from 'pages/Turns';
import RefsLinks from 'pages/Web/RefsLinks';
import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { Navigate, Outlet, Route, Routes } from 'react-router-dom';
import { getUserFullname } from 'utils/auth';

// Lazy loaded pages
const Clients = React.lazy(() => import('pages/Clients/AllClients'));
const SourceDocuments = React.lazy(() =>
  import('pages/Documents/SourceDocuments')
);
const DigitalAccountInvoice = React.lazy(() =>
  import('pages/Documents/DigitalAccountInvoice')
);
const DigitalActOfCompletion = React.lazy(() =>
  import('pages/Documents/DigitalActOfCompletion')
);

const Form910Page = React.lazy(() => import('pages/Reports/Form910Page'));
const Form913Page = React.lazy(() => import('pages/Reports/Form913Page'));
const VideoInstructions = React.lazy(() =>
  import('pages/Web/VideoInstructions')
);
const Questions = React.lazy(() => import('pages/Web/Questions'));
const FAQ = React.lazy(() => import('pages/Web/FAQ'));
const Tilda = React.lazy(() => import('pages/Web/Tilda'));
const DealDocuments = React.lazy(() => import('pages/Documents/DealDocuments'));

function App() {
  const { isLoggedIn, user } = useSelector((state) => state.auth);
  const { identifyUser } = useMixpanel();

  useEffect(() => {
    if (isLoggedIn) {
      const { id, role, username } = user;
      identifyUser(id, {
        fullName: getUserFullname(user),
        role,
        email: username,
      });
    }
  }, [isLoggedIn]);

  // Notification listener which listens firebase for showing notifications
  onMessageListener().then((data) => {
    const { title, body } = data.notification;
    notification.info({ message: title, description: body, duration: 0 });

    const audio = new Audio('./assets/audio/notification.mp3');
    audio.play();
  });

  return (
    <Routes>
      <Route path="/" element={<Navigate to="/login" />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/recovery" element={<RecoveryPage />} />

      <Route element={<PrivateRoutes redirectPath="/login" />}>
        <Route path="/setting/logs" element={<ClientsLogs />} />
        <Route path="session-history" element={<HistorySessions />} />
        <Route
          path="/setting/access-manage/roles/create"
          element={<RolesFormPage formTitle="Создание роли" />}
        />
        <Route
          path="/setting/access-manage/roles/edit/:id"
          element={<RolesFormPage formTitle="Редактирование роли" />}
        />
        <Route path="/setting/access-manage/*" element={<Outlet />}>
          <Route path="users" element={<Users />} />
          <Route path="roles" element={<Roles />} />
          <Route path="modules" element={<Modules />} />
          <Route path="permissions" element={<Permissions />} />
          <Route path="tribes" element={<Tribes />} />
          <Route path="groups" element={<Groups />} />
          <Route path="*" element={<NotFoundPage />} />
        </Route>
        <Route path="/setting/integration/*" element={<Outlet />}>
          <Route path="bcc" element={<BCC />} />
          <Route path="kgd" element={<KGD />} />
          <Route path="firebase" element={<Firebase />} />
          <Route path="smsc" element={<SMS />} />
          <Route path="*" element={<NotFoundPage />} />
        </Route>
        <Route path="clients/*" element={<Outlet />}>
          <Route
            path="all-clients"
            element={
              <React.Suspense fallback={<Spin />}>
                <Clients />
              </React.Suspense>
            }
          />
          <Route path="employees/*" element={<Outlet />}>
            <Route path="all-employees" element={<Employees />} />
            <Route path="records" element={<EmployeesRecords />} />
            <Route path="*" element={<NotFoundPage />} />
          </Route>
          <Route path="accruals" element={<Accruals />} />
          <Route path="bank-accounts" element={<BankAccounts />} />
          <Route path="product-services" element={<ProductServices />} />
          <Route path="contractors" element={<Contractors />} />
          <Route path="*" element={<NotFoundPage />} />
        </Route>
        <Route path="/setting/services/*" element={<Outlet />}>
          <Route path="new-services" element={<NewServices />} />
          <Route path="options" element={<Options />} />
          <Route path="discount" element={<Discount />} />
          <Route path="promocodes" element={<Promocodes />} />
          <Route path="promo" element={<Promo />} />
          <Route path="packages" element={<Package />} />
          <Route path="create" element={<PackageCreate />} />
          <Route path="edit/:id" element={<PackageEdit />} />
          <Route path="*" element={<NotFoundPage />} />
        </Route>
        <Route path="/setting/push-manager/*" element={<Outlet />}>
          <Route path="segments" element={<Segments />} />
          <Route path="notification" element={<PushNotification />} />
          <Route path="*" element={<NotFoundPage />} />
        </Route>
        <Route path="/setting/web/*" element={<Outlet />}>
          <Route
            path="video-instructions"
            element={
              <React.Suspense fallback={<Spin />}>
                <VideoInstructions />
              </React.Suspense>
            }
          />
          <Route
            path="questions"
            element={
              <React.Suspense fallback={<Spin />}>
                <Questions />
              </React.Suspense>
            }
          />
          <Route
            path="faq"
            element={
              <React.Suspense fallback={<Spin />}>
                <FAQ />
              </React.Suspense>
            }
          />
          <Route
            path="tilda"
            element={
              <React.Suspense fallback={<Spin />}>
                <Tilda />
              </React.Suspense>
            }
          />
          <Route path="refs-links" element={<RefsLinks />} />
        </Route>
        <Route path="/setting/mobile/*" element={<Outlet />}>
          <Route path="stories" element={<Stories />} />
          <Route path="story-groups" element={<StoryGroup />} />
          <Route path="main-events" element={<MainEvents />} />
          <Route path="app-version" element={<AppVersion />} />
          <Route path="*" element={<NotFoundPage />} />
        </Route>
        <Route path="documents/*" element={<Outlet />}>
          <Route
            path="source-documents"
            element={
              <React.Suspense fallback={<Spin />}>
                <SourceDocuments />
              </React.Suspense>
            }
          />
          <Route
            path="digital-account-invoice"
            element={
              <React.Suspense fallback={<Spin />}>
                <DigitalAccountInvoice />
              </React.Suspense>
            }
          />
          <Route
            path="digital-act-of-completion"
            element={
              <React.Suspense fallback={<Spin />}>
                <DigitalActOfCompletion />
              </React.Suspense>
            }
          />

          <Route
            path="deal-documents"
            element={
              <React.Suspense fallback={<Spin />}>
                <DealDocuments />
              </React.Suspense>
            }
          />
          <Route
            path="act-sverki"
            element={
              <React.Suspense fallback={<Spin />}>
                <ActSverki />
              </React.Suspense>
            }
          />
          <Route path="*" element={<NotFoundPage />} />
        </Route>
        <Route path="reports/*" element={<Outlet />}>
          <Route
            path="910"
            element={
              <React.Suspense fallback={<Spin />}>
                <Form910Page />
              </React.Suspense>
            }
          />
          <Route
            path="913"
            element={
              <React.Suspense fallback={<Spin />}>
                <Form913Page />
              </React.Suspense>
            }
          />
          <Route path="diagnostic" element={<Diagnostic />} />
        </Route>
        <Route path="payments/*" element={<Outlet />}>
          <Route path="sales" element={<History />} />
          <Route path="subscribers" element={<Subscribers />} />
          <Route path="kassa" element={<Kassa />} />
          <Route path="turns" element={<Turns />} />
          <Route path="bank-statements" element={<BankStatements />} />
          <Route path="statistics/*" element={<Outlet />}>
            <Route path="sales" element={<Sales />} />
          </Route>
          <Route path="card-payments" element={<CardPayments />} />
          <Route path="pay-taxes" element={<PayedTaxes />} />
          <Route
            path="card-payments-reversal"
            element={<CardReversalPayments />}
          />
          <Route path="client-payments" element={<ClientPayments />} />
          <Route path="bcc-payments" element={<BccPayments />} />
          <Route path="bcc-refund" element={<BccPaymentsRefund />} />
          <Route path="*" element={<NotFoundPage />} />
        </Route>
        <Route path="setting/handbooks/*" element={<Outlet />}>
          <Route path="partners" element={<Partners />} />
          <Route path="calendar" element={<HandbookCalendar />} />
          <Route path="bvu" element={<BVU />} />
          <Route path="kbk" element={<KBK />} />
          <Route path="knp" element={<KNP />} />
          <Route path="oked" element={<OKED />} />
          <Route path="kato" element={<KATO />} />
          <Route path="rrp" element={<RRP />} />
          <Route path="prp" element={<PRP />} />
          <Route path="balance" element={<Balance />} />
          <Route path="accounts" element={<PartnerAccounts />} />
          <Route path="ugd" element={<UGD />} />
          <Route path="gsvs" element={<GSVS />} />;
          <Route path="*" element={<NotFoundPage />} />
        </Route>
        <Route path="/edo" element={<EdoAlertConfig />} />
      </Route>

      <Route path="*" element={<NotFoundPage />} />
      <Route path="no-access" element={<NoAccessPage />} />
    </Routes>
  );
}

export default App;
